import { axiosGet } from '@/api/handlers';
import { useQuery } from '@/api/hooks';
import type { accountingType } from '@/features/master-table/models';
import { lowerCamelStringToSnake } from '@/utils';
import type {
  aggregationTargetTypeEnum,
  aggregationTypeEnum,
  analysisAxisEnum,
  analysisTargetEnum,
  sheetTypeEnum,
} from '@/utils/enum';
import type { z } from 'zod';
import { ProjectAnalysisModels } from '../_models/project';
import { queryKeyMap } from '../_utils/query-keys';

type AnalysisAxis = z.infer<typeof analysisAxisEnum>;

// NOTE: 配列はカンマ区切りで送信する
type ProjectAnalysisRequestParams = {
  fiscalYearRange: string; // カンマ区切りの2つの年度（例: "2023,2024"）
  analysisTarget: z.infer<typeof analysisTargetEnum>;
  aggregationType: z.infer<typeof aggregationTypeEnum>;
  aggregationTargetType: z.infer<typeof aggregationTargetTypeEnum>;
  analysisAxis: Exclude<AnalysisAxis, 'year'> | null;
  cascader: string | null; // UUID形式
  sheetType: z.infer<typeof sheetTypeEnum> | null;
  accountingType: z.infer<typeof accountingType> | null;
  accountingTypeIds: string | null; // カンマ区切りの文字列
  implementationMethods: string | null; // カンマ区切りの文字列
  projectNumbers: string | null; // カンマ区切りの文字列
  textSearchCriteria: string | null; // カンマ区切りの文字列
  q: string | null; // 検索キーワード
  corporateNumber: string | null; // 法人番号
  categories: string | null; // カンマ区切りのカテゴリID
  // 以下PS用。カンマ区切りの文字列
  organizationIds: string | null;
  policyIds: string | null;
  otherResponsibleOrganizationIds: string | null;
  expenditureBudgetPurposeIds: string | null;
  endYear: number | null;
  startYear: number | null;
};

export const useProjectAnalysis = ({
  params,
}: {
  params: ProjectAnalysisRequestParams;
}) => {
  const baseURL = '/api/project-analysis/';
  return useQuery({
    queryKey: queryKeyMap.projectAnalysis(params),
    queryFn: async () => {
      // aggregationTypeの値をcamelCaseからsnake_caseに変換
      const paramsWithSnakeCase = {
        ...params,
        aggregationType: lowerCamelStringToSnake(params.aggregationType),
      };
      const projectAnalysis = await axiosGet(baseURL, paramsWithSnakeCase);
      return ProjectAnalysisModels.parse(projectAnalysis);
    },
  });
};
